import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { toast } from "sonner";
import { formatDate } from "@/lib/format";
import PhotoUploader from "@/components/PhotoUploader";
import DocPreview from "@/components/DocPreview";
import { Plus, MagnifyingGlass, UserCircle, PencilSimple, Trash, UploadSimple, Eye, GraduationCap, Books, Users, FileText, ClockCounterClockwise } from "@phosphor-icons/react";

const EMPTY = {
  first_name: "", last_name: "", birth_date: "", gender: "M",
  nationality: "Burkinabè", address: "", photo_url: "", class_id: "",
  matricule: "", code_permanent: "", notes: "",
};

const STATUS_LABEL = {
  active:       { l: "Actif",       c: "bg-secondary text-secondary-foreground" },
  graduated:    { l: "Diplômé",     c: "bg-primary/15 text-primary" },
  transferred:  { l: "Transféré",   c: "bg-accent/15 text-accent" },
  dropped_out:  { l: "Abandon",     c: "bg-destructive/10 text-destructive" },
  archived:     { l: "Archivé",     c: "bg-muted text-muted-foreground" },
};
const ADMIN_ROLES = new Set(["super_admin", "owner", "director", "study_director", "secretary", "admin"]);

export default function Students() {
  const { user } = useAuth();
  const canAdmin = user && ADMIN_ROLES.has(user.role);
  const [students, setStudents] = useState([]);
  const [classes, setClasses] = useState([]);
  const [q, setQ] = useState("");
  const [classFilter, setClassFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("active");
  const [open, setOpen] = useState(false);
  const [openCsv, setOpenCsv] = useState(false);
  const [csvText, setCsvText] = useState("");
  const [csvReplace, setCsvReplace] = useState(false);
  const [importing, setImporting] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [profile, setProfile] = useState(null);   // { student, classes, enrollments, courses }
  const [profileLoading, setProfileLoading] = useState(false);
  // Phase 4 — lifecycle status & admission documents
  const [statusTarget, setStatusTarget] = useState(null);
  const [statusForm, setStatusForm] = useState({ status: "active", reason: "", graduation_date: "" });
  const [statusSaving, setStatusSaving] = useState(false);
  const [docsTarget, setDocsTarget] = useState(null);
  const [docs, setDocs] = useState([]);
  const [docsLoading, setDocsLoading] = useState(false);
  const [previewDoc, setPreviewDoc] = useState(null);

  const openProfile = async (s) => {
    setProfile({ student: s, classes: [], enrollments: [], courses: [] });
    setProfileLoading(true);
    try {
      const r = await api.get(`/students/${s.id}/profile`);
      setProfile(r.data);
    } catch (e) { toast.error(formatApiError(e)); setProfile(null); }
    finally { setProfileLoading(false); }
  };

  const load = async () => {
    try {
      const params = { include_archived: true };
      if (classFilter !== "all") params.class_id = classFilter;
      if (q) params.search = q;
      const [s, c] = await Promise.all([
        api.get("/students", { params }),
        api.get("/classes"),
      ]);
      setStudents(s.data); setClasses(c.data);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [q, classFilter]);

  const filteredStudents = students.filter(s => {
    if (statusFilter === "all") return true;
    return (s.status || "active") === statusFilter;
  });

  const openStatus = (s) => {
    setStatusTarget(s);
    setStatusForm({
      status: s.status || "active",
      reason: "",
      graduation_date: s.graduation_date || "",
    });
  };
  const saveStatus = async () => {
    if (!statusTarget) return;
    if (statusForm.status === "graduated" && !statusForm.graduation_date) {
      return toast.error("Date de diplomation requise pour ce statut");
    }
    setStatusSaving(true);
    try {
      const payload = { status: statusForm.status, reason: statusForm.reason };
      if (statusForm.status === "graduated") payload.graduation_date = statusForm.graduation_date;
      await api.patch(`/students/${statusTarget.id}/status`, payload);
      toast.success(`Statut mis à jour : ${STATUS_LABEL[statusForm.status]?.l}`);
      setStatusTarget(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setStatusSaving(false); }
  };

  const openDocs = async (s) => {
    setDocsTarget(s);
    setDocs([]);
    setDocsLoading(true);
    try {
      const r = await api.get(`/students/${s.id}/admission-documents`);
      setDocs(r.data || []);
    } catch (e) { toast.error(formatApiError(e)); setDocsTarget(null); }
    finally { setDocsLoading(false); }
  };

  const openCreate = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (s) => {
    setEditing(s);
    setForm({ ...EMPTY, ...s });
    setOpen(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form };
      if (payload.class_id === "none" || !payload.class_id) delete payload.class_id;
      if (editing) await api.patch(`/students/${editing.id}`, payload);
      else await api.post("/students", payload);
      toast.success(editing ? "Élève modifié" : "Élève créé");
      setOpen(false);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const del = async (s) => {
    if (!window.confirm(`Supprimer ${s.first_name} ${s.last_name} ? Si l'élève a des notes/paiements, il sera archivé (soft-delete) pour préserver l'historique.`)) return;
    try {
      const r = await api.delete(`/students/${s.id}`);
      if (r.data?.archived) toast.info(`Élève archivé (${r.data.linked_records} enregistrements liés préservés)`);
      else toast.success("Élève supprimé");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const restore = async (s) => {
    try { await api.post(`/students/${s.id}/restore`); toast.success("Élève restauré"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const doImport = async () => {
    if (!csvText.trim()) return toast.error("Collez un CSV ou téléversez un fichier");
    if (csvReplace && !window.confirm("Attention : cette option supprime TOUS les élèves existants avant l'import. Continuer ?")) return;
    setImporting(true);
    try {
      const { data } = await api.post("/students/import-csv", { csv_text: csvText, replace_demo: csvReplace });
      if (data.error_count > 0) {
        toast.warning(`${data.created} élève(s) importé(s) · ${data.error_count} ligne(s) en erreur`);
      } else {
        toast.success(`${data.created} élève(s) importé(s) avec succès`);
      }
      setOpenCsv(false); setCsvText(""); setCsvReplace(false); load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setImporting(false); }
  };

  const onCsvFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const reader = new FileReader();
    reader.onload = () => setCsvText(String(reader.result || ""));
    reader.readAsText(f);
  };

  const classById = Object.fromEntries(classes.map(c => [c.id, c]));

  return (
    <div className="p-6 lg:p-10 max-w-7xl mx-auto">
      <header className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <div className="text-xs uppercase tracking-widest text-secondary font-semibold">Élèves</div>
          <h1 className="mt-2 font-heading text-3xl font-bold tracking-tight">
            {students.length} élève{students.length > 1 ? "s" : ""}
          </h1>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Dialog open={openCsv} onOpenChange={setOpenCsv}>
            <DialogTrigger asChild>
              <Button variant="outline" className="rounded-full" data-testid="btn-import-csv">
                <UploadSimple size={14} className="mr-2" /> Importer CSV
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl" data-testid="dialog-csv">
              <DialogHeader><DialogTitle>Importer des élèves depuis un CSV</DialogTitle></DialogHeader>
              <div className="space-y-4">
                <div className="p-3 bg-muted rounded-md text-xs space-y-1">
                  <div className="font-semibold text-foreground">Colonnes reconnues (en-têtes FR ou EN) :</div>
                  <div className="font-mono text-muted-foreground">
                    matricule, first_name/prenom, last_name/nom, gender/sexe (M ou F),<br/>
                    birth_date/date_naissance, classe (nom exact), nationalite, adresse, notes
                  </div>
                  <div className="text-muted-foreground mt-2">Le nom de classe doit correspondre à une classe existante (ex: <code>CP1</code>, <code>6ème A</code>).</div>
                </div>
                <div>
                  <Label>Téléverser un fichier CSV</Label>
                  <Input type="file" accept=".csv,text/csv" onChange={e => {
                    const f = e.target.files?.[0]; if (!f) return;
                    const reader = new FileReader();
                    reader.onload = () => setCsvText(String(reader.result || ""));
                    reader.readAsText(f);
                  }} data-testid="input-csv-file" />
                </div>
                <div>
                  <Label>Ou collez le contenu CSV</Label>
                  <textarea
                    value={csvText} onChange={e => setCsvText(e.target.value)}
                    rows={8}
                    placeholder={"matricule,first_name,last_name,gender,classe\nWP-001,Awa,Sanogo,F,CP1"}
                    className="w-full mt-1.5 p-2 border border-border rounded-md font-mono text-xs bg-background"
                    data-testid="textarea-csv"
                  />
                </div>
                <label className="flex items-center gap-2 cursor-pointer p-2 rounded-md border border-destructive/30 bg-destructive/5">
                  <input type="checkbox" checked={csvReplace} onChange={e => setCsvReplace(e.target.checked)} className="w-4 h-4 accent-destructive" data-testid="check-replace" />
                  <span className="text-xs text-destructive">
                    Remplacer TOUS les élèves existants (irréversible — supprime la démo)
                  </span>
                </label>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setOpenCsv(false)}>Annuler</Button>
                <Button onClick={doImport} disabled={importing} className="bg-accent hover:bg-accent/90 text-accent-foreground" data-testid="btn-do-import">
                  <UploadSimple size={14} className="mr-1" /> {importing ? "Import…" : "Importer"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button onClick={openCreate} className="rounded-full bg-accent hover:bg-accent/90 text-accent-foreground" data-testid="btn-new-student">
              <Plus size={16} className="mr-2" /> Nouvel élève
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl" data-testid="dialog-student">
            <DialogHeader><DialogTitle>{editing ? "Modifier l'élève" : "Nouvel élève"}</DialogTitle></DialogHeader>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Prénom" value={form.first_name} onChange={v => setForm({ ...form, first_name: v })} testid="input-first-name" />
              <Field label="Nom" value={form.last_name} onChange={v => setForm({ ...form, last_name: v })} testid="input-last-name" />
              <Field label="Matricule (auto si vide)" value={form.matricule} onChange={v => setForm({ ...form, matricule: v })} testid="input-matricule" />
              <Field label="Date de naissance" type="date" value={form.birth_date} onChange={v => setForm({ ...form, birth_date: v })} testid="input-birth" />
              <div>
                <Label>Sexe</Label>
                <Select value={form.gender} onValueChange={v => setForm({ ...form, gender: v })}>
                  <SelectTrigger className="mt-1.5" data-testid="select-gender"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="M">Masculin</SelectItem>
                    <SelectItem value="F">Féminin</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Field label="Nationalité" value={form.nationality} onChange={v => setForm({ ...form, nationality: v })} testid="input-nationality" />
              <Field label="Code permanent (facultatif)" value={form.code_permanent} onChange={v => setForm({ ...form, code_permanent: v })} testid="input-code-permanent" />
              <div className="md:col-span-2">
                <Label>Photo de l'élève</Label>
                <div className="mt-1.5">
                  <PhotoUploader value={form.photo_url} onChange={v => setForm({ ...form, photo_url: v })} kind="student" size={72} />
                </div>
              </div>
              <div className="md:col-span-2">
                <Label>Classe</Label>
                <Select value={form.class_id || "none"} onValueChange={v => setForm({ ...form, class_id: v === "none" ? "" : v })}>
                  <SelectTrigger className="mt-1.5" data-testid="select-class"><SelectValue placeholder="Non affecté" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Non affecté</SelectItem>
                    {classes.map(c => <SelectItem key={c.id} value={c.id}>{c.name} · {c.level}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <Field label="Adresse" value={form.address} onChange={v => setForm({ ...form, address: v })} testid="input-address" className="md:col-span-2" />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>Annuler</Button>
              <Button onClick={save} disabled={saving} className="bg-accent hover:bg-accent/90 text-accent-foreground" data-testid="btn-save-student">
                {saving ? "Enregistrement…" : "Enregistrer"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        </div>
      </header>

      <Card className="p-4 mb-4 flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-[220px]">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input value={q} onChange={e => setQ(e.target.value)} placeholder="Rechercher par nom ou matricule…" className="pl-9" data-testid="input-search" />
        </div>
        <Select value={classFilter} onValueChange={setClassFilter}>
          <SelectTrigger className="w-[180px]" data-testid="select-class-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Toutes les classes</SelectItem>
            {classes.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[160px]" data-testid="select-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous les statuts</SelectItem>
            {Object.entries(STATUS_LABEL).map(([k, v]) => (
              <SelectItem key={k} value={k}>{v.l}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </Card>

      <div className="bg-card border border-border rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left text-xs uppercase tracking-wider text-muted-foreground">
                <th className="px-4 py-3 font-semibold">Élève</th>
                <th className="px-4 py-3 font-semibold">Matricule</th>
                <th className="px-4 py-3 font-semibold">Classe</th>
                <th className="px-4 py-3 font-semibold">Statut</th>
                <th className="px-4 py-3 font-semibold">Genre</th>
                <th className="px-4 py-3 font-semibold">Inscrit le</th>
                <th className="px-4 py-3 font-semibold text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredStudents.map((s, i) => {
                const st = STATUS_LABEL[s.status || "active"] || STATUS_LABEL.active;
                return (
                <tr key={s.id} className={`border-b border-border last:border-0 ${i % 2 ? "bg-muted/30" : ""}`} data-testid={`row-student-${s.id}`}>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      {s.photo_url ? (
                        <img src={s.photo_url.startsWith("http") ? s.photo_url : `${process.env.REACT_APP_BACKEND_URL}${s.photo_url}`} alt="" className="w-9 h-9 rounded-full object-cover" loading="lazy" />
                      ) : (
                        <div className="w-9 h-9 rounded-full bg-muted flex items-center justify-center">
                          <UserCircle size={22} weight="duotone" className="text-muted-foreground" />
                        </div>
                      )}
                      <div>
                        <div className="font-medium">{s.first_name} {s.last_name}</div>
                        <div className="text-xs text-muted-foreground">{s.nationality}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 font-mono text-xs">
                    {s.matricule}
                    {s.code_permanent && <div className="text-[10px] text-muted-foreground mt-0.5">CP: {s.code_permanent}</div>}
                  </td>
                  <td className="px-4 py-3">{classById[s.class_id]?.name || <span className="text-muted-foreground italic">non affecté</span>}</td>
                  <td className="px-4 py-3">
                    <Badge className={`${st.c} text-[10px]`} data-testid={`badge-status-${s.id}`}>{st.l}</Badge>
                    {s.graduation_date && s.status === "graduated" && (
                      <div className="text-[10px] text-muted-foreground mt-0.5">📅 {s.graduation_date}</div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-xs">{s.gender === "M" ? "Masculin" : "Féminin"}</td>
                  <td className="px-4 py-3 text-xs text-muted-foreground">{formatDate(s.enrollment_date)}</td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex justify-end gap-1">
                      <Button variant="ghost" size="icon" onClick={() => openProfile(s)} data-testid={`btn-view-${s.id}`} title="Voir le récap"><Eye size={16} className="text-primary" /></Button>
                      {canAdmin && (
                        <Button variant="ghost" size="icon" onClick={() => openDocs(s)} data-testid={`btn-docs-${s.id}`} title="Documents d'admission">
                          <FileText size={16} className="text-primary" />
                        </Button>
                      )}
                      {canAdmin && (
                        <Button variant="ghost" size="icon" onClick={() => openStatus(s)} data-testid={`btn-status-${s.id}`} title="Changer le statut">
                          <ClockCounterClockwise size={16} className="text-accent" />
                        </Button>
                      )}
                      <Button variant="ghost" size="icon" onClick={() => openEdit(s)} data-testid={`btn-edit-${s.id}`}><PencilSimple size={16} /></Button>
                      <Button variant="ghost" size="icon" onClick={() => del(s)} data-testid={`btn-delete-${s.id}`}><Trash size={16} className="text-destructive" /></Button>
                    </div>
                  </td>
                </tr>
                );
              })}
              {!filteredStudents.length && (
                <tr><td colSpan={7} className="px-4 py-12 text-center text-sm text-muted-foreground">Aucun élève trouvé.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Phase 4 — Change lifecycle status */}
      <Dialog open={!!statusTarget} onOpenChange={(v) => { if (!v) setStatusTarget(null); }}>
        <DialogContent data-testid="dialog-status">
          <DialogHeader>
            <DialogTitle>Changer le statut · {statusTarget?.first_name} {statusTarget?.last_name}</DialogTitle>
            <DialogDescription>
              Le cycle de vie de l'élève : Actif → Diplômé / Transféré / Abandon / Archivé. Les élèves diplômés depuis 3+ ans sont archivés automatiquement chaque semaine.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-3">
            <div>
              <Label>Nouveau statut</Label>
              <Select value={statusForm.status} onValueChange={v => setStatusForm({ ...statusForm, status: v })}>
                <SelectTrigger data-testid="select-new-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(STATUS_LABEL).map(([k, v]) => (
                    <SelectItem key={k} value={k}>{v.l}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {statusForm.status === "graduated" && (
              <div>
                <Label>Date de diplomation *</Label>
                <Input type="date" value={statusForm.graduation_date}
                       onChange={e => setStatusForm({ ...statusForm, graduation_date: e.target.value })}
                       data-testid="input-grad-date" />
              </div>
            )}
            <div>
              <Label>Motif (facultatif — visible dans le journal)</Label>
              <Textarea rows={3} value={statusForm.reason}
                        onChange={e => setStatusForm({ ...statusForm, reason: e.target.value })}
                        placeholder="Ex : transféré au Lycée Y, fin de scolarité, abandon volontaire…"
                        data-testid="input-status-reason" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setStatusTarget(null)}>Annuler</Button>
            <Button onClick={saveStatus} disabled={statusSaving} data-testid="btn-save-status">
              {statusSaving ? "Enregistrement…" : "Valider"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Phase 3+4 — View admission documents attached to student */}
      <Dialog open={!!docsTarget} onOpenChange={(v) => { if (!v) setDocsTarget(null); }}>
        <DialogContent className="max-w-2xl" data-testid="dialog-docs">
          <DialogHeader>
            <DialogTitle>Documents d'admission · {docsTarget?.first_name} {docsTarget?.last_name}</DialogTitle>
            <DialogDescription>
              Pièces jointes rattachées à la candidature d'origine. Accès réservé à la direction et au secrétariat.
            </DialogDescription>
          </DialogHeader>
          {docsLoading ? (
            <div className="py-8 text-center text-sm text-muted-foreground">Chargement…</div>
          ) : docs.length === 0 ? (
            <div className="py-8 text-center text-sm text-muted-foreground">
              Aucun document d'admission. L'élève a peut-être été créé manuellement (import CSV ou création directe).
            </div>
          ) : (
            <div className="space-y-2">
              {docs.map(d => (
                <div key={d.id} className="flex items-center justify-between gap-3 p-3 rounded border border-border bg-muted/30">
                  <div className="flex items-center gap-2 min-w-0 flex-1">
                    <FileText size={18} className="text-primary shrink-0" />
                    <div className="min-w-0">
                      <div className="font-medium truncate">{d.label || d.key}</div>
                      <div className="text-xs text-muted-foreground truncate">{d.filename} · {(d.size / 1024).toFixed(1)} Ko</div>
                    </div>
                  </div>
                  <Badge className={d.status === "verified" ? "bg-secondary text-secondary-foreground" : d.status === "refused" ? "bg-destructive/10 text-destructive" : "bg-muted text-muted-foreground"}>
                    {d.status === "verified" ? "✓ Vérifié" : d.status === "refused" ? "✗ Refusé" : "⏳ En attente"}
                  </Badge>
                  <Button variant="outline" size="sm" onClick={() => setPreviewDoc(d)}
                          className="text-xs shrink-0" data-testid={`btn-preview-doc-${d.id}`}>
                    <Eye size={12} className="mr-1" /> Prévisualiser
                  </Button>
                </div>
              ))}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDocsTarget(null)}>Fermer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <DocPreview doc={previewDoc} onClose={() => setPreviewDoc(null)} />
    </div>
  );
}

function Field({ label, value, onChange, type = "text", className = "", testid }) {
  return (
    <div className={className}>
      <Label>{label}</Label>
      <Input type={type} value={value || ""} onChange={e => onChange(e.target.value)} className="mt-1.5" data-testid={testid} />
    </div>
  );
}
